import { useEffect, useRef, useState } from "react";
import type { ReactNode } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useAuthStore } from "../../features/auth/stores/authStore";
import {
  createSignalRConnection,
  HubConnectionState,
} from "../services/signalr";
import type { HubConnection } from "../services/signalr";
import type { EntityChangeNotification } from "../types/signalr";
import {
  handleEntityChange,
  invalidateAllOnReconnect,
} from "../services/realtimeInvalidation";
import { SignalRContext } from "./signalr-context";

export function SignalRProvider({ children }: { children: ReactNode }) {
  const queryClient = useQueryClient();
  const { isAuthenticated } = useAuthStore();
  const connectionRef = useRef<HubConnection | null>(null);
  const [connectionState, setConnectionState] = useState<HubConnectionState>(
    HubConnectionState.Disconnected
  );

  useEffect(() => {
    if (!isAuthenticated) {
      if (connectionRef.current) {
        connectionRef.current.stop();
        connectionRef.current = null;
        setConnectionState(HubConnectionState.Disconnected);
      }
      return;
    }

    const connection = createSignalRConnection();
    connectionRef.current = connection;

    connection.on("EntityChanged", (notification: EntityChangeNotification) => {
      handleEntityChange(queryClient, notification);
    });

    connection.onreconnecting(() => {
      setConnectionState(HubConnectionState.Reconnecting);
    });

    connection.onreconnected(() => {
      setConnectionState(HubConnectionState.Connected);
      invalidateAllOnReconnect(queryClient);
    });

    connection.onclose(() => {
      setConnectionState(HubConnectionState.Disconnected);
    });

    connection
      .start()
      .then(() => {
        setConnectionState(HubConnectionState.Connected);
        // Sweep the cache on initial connect too, not just on reconnect.
        // If anything changed between auth-restored and the socket coming up
        // (page refresh, tab restore, second login), the first mount of any
        // query will refetch and pick it up.
        invalidateAllOnReconnect(queryClient);
      })
      .catch(() => setConnectionState(HubConnectionState.Disconnected));

    return () => {
      connection.stop();
      connectionRef.current = null;
    };
  }, [isAuthenticated, queryClient]);

  return (
    <SignalRContext.Provider value={{ connectionState }}>
      {children}
    </SignalRContext.Provider>
  );
}
